/** Column metadata for each client-portal section.
 * The shape determines:
 *  - Display order in tables
 *  - Filter widget type (text / date / number / select)
 *  - Numeric formatting (₹ vs plain)
 *  - Alignment (right for amounts)
 *
 *  IMPORTANT: keys MUST match the backend `_parse_row` field names.
 */

export const SECTION_META = {
  sales_b2b: {
    label: "Sales B2B",
    columns: [
      { key: "invoice_date", label: "Invoice Date", type: "date" },
      { key: "invoice_number", label: "Invoice #", type: "text" },
      { key: "customer_billing_name", label: "Customer", type: "text" },
      { key: "customer_billing_gstin", label: "GSTIN", type: "text" },
      { key: "state", label: "State", type: "select" },
      { key: "place_of_supply", label: "Place of Supply", type: "select" },
      { key: "hsn_sac", label: "HSN/SAC", type: "text" },
      { key: "total_taxable_value", label: "Taxable Value", type: "currency" },
      { key: "cgst_rate", label: "CGST %", type: "number" },
      { key: "cgst_amount", label: "CGST ₹", type: "currency" },
      { key: "sgst_rate", label: "SGST %", type: "number" },
      { key: "sgst_amount", label: "SGST ₹", type: "currency" },
      { key: "igst_rate", label: "IGST %", type: "number" },
      { key: "igst_amount", label: "IGST ₹", type: "currency" },
      { key: "cess_rate", label: "CESS %", type: "number" },
      { key: "cess_amount", label: "CESS ₹", type: "currency" },
      { key: "total_invoice_value", label: "Invoice Total", type: "currency", bold: true },
    ],
  },
  sales_b2c: {
    label: "Sales B2C",
    columns: [
      { key: "invoice_date", label: "Invoice Date", type: "date" },
      { key: "invoice_number", label: "Invoice #", type: "text" },
      { key: "customer_billing_name", label: "Customer", type: "text" },
      { key: "customer_billing_gstin", label: "GSTIN", type: "text" },
      { key: "state", label: "State", type: "select" },
      { key: "place_of_supply", label: "Place of Supply", type: "select" },
      { key: "hsn_sac", label: "HSN/SAC", type: "text" },
      { key: "total_taxable_value", label: "Taxable Value", type: "currency" },
      { key: "cgst_rate", label: "CGST %", type: "number" },
      { key: "cgst_amount", label: "CGST ₹", type: "currency" },
      { key: "sgst_rate", label: "SGST %", type: "number" },
      { key: "sgst_amount", label: "SGST ₹", type: "currency" },
      { key: "igst_rate", label: "IGST %", type: "number" },
      { key: "igst_amount", label: "IGST ₹", type: "currency" },
      { key: "cess_rate", label: "CESS %", type: "number" },
      { key: "cess_amount", label: "CESS ₹", type: "currency" },
      { key: "total_invoice_value", label: "Invoice Total", type: "currency", bold: true },
    ],
  },
  sales_returns: {
    label: "Sales Return / Credit Note",
    columns: [
      { key: "invoice_date", label: "Date", type: "date" },
      { key: "credit_note_number", label: "Credit Note #", type: "text" },
      { key: "customer_billing_name", label: "Customer", type: "text" },
      { key: "customer_billing_gstin", label: "GSTIN", type: "text" },
      { key: "state", label: "State", type: "select" },
      { key: "place_of_supply", label: "Place of Supply", type: "select" },
      { key: "hsn_sac", label: "HSN/SAC", type: "text" },
      { key: "total_taxable_value", label: "Taxable Value", type: "currency" },
      { key: "cgst_rate", label: "CGST %", type: "number" },
      { key: "cgst_amount", label: "CGST ₹", type: "currency" },
      { key: "sgst_rate", label: "SGST %", type: "number" },
      { key: "sgst_amount", label: "SGST ₹", type: "currency" },
      { key: "igst_rate", label: "IGST %", type: "number" },
      { key: "igst_amount", label: "IGST ₹", type: "currency" },
      { key: "cess_rate", label: "CESS %", type: "number" },
      { key: "cess_amount", label: "CESS ₹", type: "currency" },
      { key: "total_credit_note_value", label: "Credit Note Total", type: "currency", bold: true },
    ],
  },
  missing_invoices: {
    label: "Missing Invoices",
    // Client portal view: only the first 6 source columns + the
    // auto-rendered Comments/Files column (added by SectionDataTable).
    columns: [
      { key: "gstin_of_supplier", label: "Supplier GSTIN", type: "text" },
      { key: "trade_legal_name", label: "Trade/Legal Name", type: "text" },
      { key: "invoice_number", label: "Invoice #", type: "text" },
      { key: "invoice_type", label: "Invoice Type", type: "select" },
      { key: "invoice_date", label: "Invoice Date", type: "date" },
      { key: "invoice_value", label: "Invoice Value", type: "currency" },
    ],
  },
  gstr1_unfiled: {
    label: "Invoices not filed in GSTR-1",
    columns: [
      { key: "month", label: "Month", type: "select" },
      { key: "gstin_of_supplier", label: "Supplier GSTIN", type: "text" },
      { key: "trade_legal_name", label: "Trade/Legal Name", type: "text" },
      { key: "invoice_number", label: "Invoice #", type: "text" },
      { key: "invoice_date", label: "Invoice Date", type: "date" },
      { key: "invoice_value", label: "Invoice Value", type: "currency" },
      { key: "taxable_value", label: "Taxable Value", type: "currency" },
      { key: "integrated_tax", label: "IGST", type: "currency" },
      { key: "central_tax", label: "CGST", type: "currency" },
      { key: "state_ut_tax", label: "SGST/UTGST", type: "currency" },
    ],
  },
  suspense: {
    label: "Suspense Account",
    columns: [
      { key: "entry_date", label: "Date", type: "date" },
      { key: "reference_no", label: "Reference #", type: "text" },
      { key: "description", label: "Description", type: "text" },
      { key: "debit_amount", label: "Debit", type: "currency" },
      { key: "credit_amount", label: "Credit", type: "currency" },
      { key: "status", label: "Status", type: "select" },
      { key: "resolved_date", label: "Resolved", type: "date" },
      { key: "notes", label: "Notes", type: "text" },
    ],
  },
  receivables: {
    label: "Payment Receivables",
    columns: [
      { key: "invoice_no", label: "Invoice #", type: "text" },
      { key: "customer_name", label: "Customer", type: "text" },
      { key: "invoice_date", label: "Invoice Date", type: "date" },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "amount", label: "Amount", type: "currency" },
      { key: "paid_amount", label: "Paid", type: "currency" },
      { key: "status", label: "Status", type: "select" },
      { key: "days_overdue", label: "Overdue (d)", type: "number" },
    ],
  },
  payables: {
    label: "Payment Payables",
    columns: [
      { key: "bill_no", label: "Bill #", type: "text" },
      { key: "vendor_name", label: "Vendor", type: "text" },
      { key: "bill_date", label: "Bill Date", type: "date" },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "amount", label: "Amount", type: "currency" },
      { key: "paid_amount", label: "Paid", type: "currency" },
      { key: "status", label: "Status", type: "select" },
      { key: "days_overdue", label: "Overdue (d)", type: "number" },
    ],
  },
};

export const ALL_PERMISSION_KEYS = [
  "sales_b2b", "sales_b2c", "sales_returns",
  "missing_invoices", "gstr1_unfiled", "suspense",
  "receivables", "payables", "compliance", "queries",
];

export const PERMISSION_LABELS = {
  sales_b2b: "Sales B2B",
  sales_b2c: "Sales B2C",
  sales_returns: "Sales Return / Credit Note",
  missing_invoices: "Missing Invoices",
  gstr1_unfiled: "Invoices not filed in GSTR-1",
  suspense: "Suspense Account",
  receivables: "Payment Receivables",
  payables: "Payment Payables",
  compliance: "Compliance Calendar",
  queries: "Queries / RFIs",
};
